<template>
  <div
    v-if="!hide"
    class="col"
    data-test-id="item-variation-buttons"
  >
    <div class="q-mb-sm">
      <div class="text-h5 row justify-between">
        {{ item.name }}
        <template v-if="$register">
          <q-btn
            v-if="!editMode"
            label="Edit Options"
            icon="edit"
            outline
            :loading="configuration.loading"
            @click="$emit('edit-options-button-click', () => toggleEditMode())"
          />
        </template>
      </div>
      <div> <b>Required</b> <span class="q-mx-xs"> &middot;</span> Choose 1</div>
    </div>

    <div :style="editMode ? 'border: 2px solid var(--q-info);' : ''">
      <div
        v-if="editMode"
        class="full-width row items-center q-pa-sm q-mb-md bg-info"
        :class="$isColorDark(currentSite.infoColor) ? 'text-white' : 'text-black'"
      >
        <div class="col">
          <div class="row items-center">
            <q-icon
              class="col-auto q-mr-md"
              size="md"
              name="info"
            />
            <div class="col">
              <div class="text-h5">
                Editing Sellable Item Variations
              </div>
              <div>
                Toggle each item variation to enable (on) or disable (off) sales.
              </div>
            </div>
          </div>
        </div>
        <div class="col-auto">
          <q-btn
            class="q-mr-md"
            label="Cancel"
            icon="cancel"
            outline
            color="negative"
            @click="toggleEditMode()"
          />
          <q-btn
            label="Save & Close"
            icon="save"
            color="primary"
            :loading="savingEdits || configuration.loading"
            :disable="!hasEdits"
            @click="handleSaveEdits"
          />
        </div>
      </div>

      <div
        class="row"
        :class="$q.screen.gt.xs ? 'q-col-gutter-lg' : 'q-col-gutter-sm'"
      >
        <div
          v-for="itemVariation in itemVariationsComputed"
          :key="itemVariation.id"
          class="items-start"
          :class="$q.screen.gt.xs ? 'col-6' : 'col-12'"
        >
          <q-card
            flat
            bordered
            class="position-relative"
            :class="{ 'bg-responsive-grey-2': itemOrderItem.itemVariationSelected(itemVariation) }"
            @click="handleClick(itemVariation)"
          >
            <q-card-section
              horizontal
              class="justify-between items-center q-pa-sm"
              :style="!editMode && reduceOpacity({ itemVariation }) ? 'opacity: 0.3;' : ''"
            >
              <q-icon
                :name="itemOrderItem.itemVariationSelected(itemVariation) ? 'radio_button_checked' : 'radio_button_unchecked'"
                :color="itemOrderItem.itemVariationSelected(itemVariation) ? 'primary' :'responsive'"
                class="col-auto q-mr-sm"
                :class="{ 'bg-white': itemOrderItem.itemVariationSelected(itemVariation) }"
                style="border-radius: 15px; width: 30px; height: 30px;"
                size="md"
              />
              <q-img
                v-if="itemVariation.imageUrl"
                :ratio="1"
                class="rounded-borders q-mr-sm"
                :src="itemVariation.imageUrl"
                :style="{
                  width: $q.screen.lt.sm? '20px' : '30px',
                  height: $q.screen.lt.sm? '20px' : '30px',
                  'background-color': itemVariation.color
                }"
              />

              <div
                class="col"
              >
                <div
                  :class="$q.screen.lt.sm ? 'text-h6' : 'text-h5'"
                  style="line-height: 1.5;"
                >
                  {{ itemVariation.name }}
                </div>
                <div
                  v-if="itemVariation.description"
                  class="text-caption"
                  style="line-height: 1;"
                >
                  {{ itemVariation.description }}
                </div>
              </div>
              <q-toggle
                v-if="editMode"
                :model-value="areSalesEnabled(itemVariation)"
                :disable="itemVariation.loading || configuration.loading"
                @update:model-value="toggleSalesDisabled(itemVariation)"
              />
              <slot
                v-else
                name="price"
              >
                <div
                  v-if="itemVariation.price && displayPrice"
                  class="col-auto"
                  style="min-width: 60px;"
                >
                  <i18n-n
                    class="text-subtitle1"
                    tag="span"
                    :value="itemVariation.price"
                    format="currency"
                    scope="global"
                  />
                </div>
              </slot>
            </q-card-section>
            <q-badge
              v-if="showAvailabilityBadge({ itemVariation })"
              class="absolute-top-right"
              :color="availabilityBadgeColor({ itemVariation })"
              :label="availableStock({ itemVariation })"
            />
          </q-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { SALES_ITEM_RESTRICTION_TYPES } from 'src/models/inventory-strategy/InventoryStrategy'
import Model from 'src/models/model'
import SalesRestrictionsMixin from 'src/modules/mixins/SalesRestrictionsMixin'
import { useDisableSales } from 'src/modules/register/composables/disable-sales'

export default {
  name: 'ItemVariationButtons',
  mixins: [SalesRestrictionsMixin],
  configuration: ['currentSite'],
  context: ['currentUser'],
  props: {
    orderItem: {
      type: Object,
      required: true
    },
    itemOrderItem: {
      type: Object,
      required: true
    },
    itemVariations: {
      type: Array,
      required: false,
      default: null
    },
    displayPrice: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  emits: ['click', 'request-authorization', 'edit-options-button-click'],
  setup () {
    const {
      editMode,
      toggleEditMode,
      areSalesEnabled,
      toggleSalesDisabled,
      hasEdits,
      saveEdits
    } = useDisableSales()

    return {
      editMode,
      toggleEditMode,
      areSalesEnabled,
      toggleSalesDisabled,
      hasEdits,
      saveEdits
    }
  },
  data () {
    return {
      savingEdits: false
    }
  },
  computed: {
    inventoryStrategy () {
      return this.currentSite.activeInventoryStrategy
    },
    item () {
      return this.itemOrderItem.item
    },
    itemVariationsComputed () {
      const itemVariations = this.itemVariations || this.item.itemVariations.data

      return _.filter(itemVariations, itemVariation => {
        if (!itemVariation.active) return false

        if (this.$register) return true

        if (this.inventoryStrategy?.excludeOutOfStockItemsForKioskAndConsumer && this.isOutOfStock({ itemVariation })) {
          return false
        }

        return !itemVariation.salesDisabled
      })
    },
    hide () {
      // We hide the item variation buttons if there is only one option available and it is already selected
      // This is to save space on the screen when mobile register

      const activeItemVariations = _(this.itemVariations || this.item.itemVariations.data).filter({ archivedAt: null }).value()
      const onlyOneOptionAvailable = activeItemVariations.length === 1
      const isOptionSelected = !!this.itemOrderItem.itemVariation

      if (onlyOneOptionAvailable && isOptionSelected) {
        return true
      }

      return false
    }
  },
  created () {
    _.extend(this, { configuration: Model.configuration })
  },
  methods: {
    async handleSaveEdits () {
      try {
        const selectedItemVariation = this.itemOrderItem.itemVariation
        const shouldReplaceItemVariation = !this.areSalesEnabled(selectedItemVariation)

        const restrictOutOfStock = this.inventoryStrategy?.inventoryRestrictionType === SALES_ITEM_RESTRICTION_TYPES.RESTRICT

        const replacementItemVariation = shouldReplaceItemVariation
          ? _(this.item.activeItemVariations).filter(activeItemVariation => {
            return this.areSalesEnabled(activeItemVariation) && (
              !restrictOutOfStock ||
              !this.$isOutOfStock({
                deliveryType: this.deliveryType({
                  itemVariation: activeItemVariation
                }),
                itemVariation: activeItemVariation
              })
            )
          }).first()
          : null

        this.savingEdits = true
        await this.saveEdits()

        if (shouldReplaceItemVariation && replacementItemVariation) {
          this.toggleItemVariation(replacementItemVariation)
        }
      } catch (e) {
        this.$rollbar.error(`Failed to save disable sales edits, exception: ${e.message}`)
      } finally {
        this.savingEdits = false
      }
    },
    toggleItemVariation (itemVariation) {
      this.itemOrderItem.toggleItemVariation(itemVariation)
      this.$emit('click', itemVariation)
    },
    handleClick (itemVariation) {
      if (this.editMode) return

      if (this.itemOrderItem.itemVariationSelected(itemVariation)) return

      this.checkForSalesRestrictions({
        itemVariation,
        successCallback: (adminApproved = false) => {
          this.itemOrderItem.adminApproved = adminApproved
          this.toggleItemVariation(itemVariation)
        }
      })
    }
  }
}

</script>
